interface TrackInfo {
    file: string;
    title: string;
}

export interface BGMTrackData {
    id: string;
    title: string;
}

/**
 * BGM 재생 및 분석 담당
 * HTML5 Audio 사용 (메모리 효율적)
 */
export class BGMManager {
    private bgmVolume: number;
    private isBgmPlaying: boolean;
    private currentTrack: string | null;

    // BGM 트랙 정보
    private tracks: Record<string, TrackInfo>;
    // 선택된 BGM 트랙
    private selectedTrack: string;

    // Boss mode state
    private isBossMode: boolean;
    private savedTrack: string | null;

    // HTML5 Audio 엘리먼트 (메모리 효율적)
    private htmlAudio: HTMLAudioElement | null = null;

    constructor() {
        this.bgmVolume = 0.3;
        this.isBgmPlaying = false;
        this.currentTrack = null;

        // BGM 트랙 정보
        this.tracks = {
            'Dream Veil': {
                file: 'assets/sound/Dream Veil.mp3',
                title: 'Dream Veil'
            },
            'A Memorial City': {
                file: 'assets/sound/A Memorial City.mp3',
                title: 'A Memorial City'
            },
            'Neon Lights Rendezvous': {
                file: 'assets/sound/Neon Lights Rendezvous.mp3',
                title: 'Neon Lights Rendezvous'
            }
        };

        // 선택된 BGM (기본값: Dream Veil)
        this.selectedTrack = 'Dream Veil';

        // Boss mode state
        this.isBossMode = false;
        this.savedTrack = null;
    }

    async load(trackName: string = 'Dream Veil'): Promise<void> {
        const track = this.tracks[trackName];
        if (!track) {
            console.warn(`Unknown track: ${trackName}`);
            return;
        }

        // 같은 트랙이 이미 로드되어 있으면 스킵
        const fileName = track.file.split('/').pop() || '';
        if (this.htmlAudio && this.htmlAudio.src.includes(encodeURIComponent(fileName))) {
            return;
        }

        // 기존 오디오 정리
        if (this.htmlAudio) {
            this.htmlAudio.pause();
            this.htmlAudio.src = '';
        }

        this.htmlAudio = new Audio(track.file);
        this.htmlAudio.loop = true;
        this.htmlAudio.volume = this.bgmVolume;
        this.htmlAudio.preload = 'auto';

        // 로드 완료 대기 (타임아웃 5초)
        await Promise.race([
            new Promise<void>((resolve) => {
                this.htmlAudio!.addEventListener('canplaythrough', () => resolve(), { once: true });
                this.htmlAudio!.addEventListener('error', () => resolve(), { once: true });
            }),
            new Promise<void>((resolve) => setTimeout(resolve, 5000))
        ]);
    }

    /**
     * 선택된 트랙 설정
     */
    setSelectedTrack(trackName: string): void {
        if (this.tracks[trackName]) {
            this.selectedTrack = trackName;
        }
    }

    /**
     * 현재 선택된 트랙 반환
     */
    getSelectedTrack(): string {
        return this.selectedTrack;
    }

    /**
     * 사용 가능한 모든 트랙 목록 반환
     */
    getAllTracks(): BGMTrackData[] {
        return Object.entries(this.tracks).map(([id, info]) => ({
            id,
            title: info.title
        }));
    }

    async loadSelectedTrack(): Promise<void> {
        await this.load(this.selectedTrack);
    }

    async play(): Promise<void> {
        const trackName = this.selectedTrack;

        // 같은 트랙이 이미 재생 중이면 스킵
        if (this.isBgmPlaying && this.currentTrack === trackName) return;

        // 기존 BGM이 있으면 정지
        this.stop();

        try {
            await this.load(trackName);

            if (this.htmlAudio) {
                await this.htmlAudio.play();
                this.isBgmPlaying = true;
                this.currentTrack = trackName;
            }
        } catch (e) {
            console.warn('Failed to play BGM:', e);
        }
    }

    getCurrentTrackTitle(): string | null {
        if (!this.currentTrack) return null;
        return this.tracks[this.currentTrack]?.title || null;
    }

    stop(): void {
        if (this.htmlAudio) {
            this.htmlAudio.pause();
            this.htmlAudio.currentTime = 0;
        }
        this.isBgmPlaying = false;
        this.currentTrack = null;
    }

    /**
     * BGM 일시정지 (포커스 아웃 시)
     */
    pause(): void {
        if (this.htmlAudio && this.isBgmPlaying) {
            this.htmlAudio.pause();
        }
    }

    /**
     * BGM 재개 (포커스 인 시)
     */
    resume(): void {
        if (this.htmlAudio && this.isBgmPlaying) {
            this.htmlAudio.play().catch(() => {
                // 자동 재생 정책으로 실패할 수 있음
            });
        }
    }

    setVolume(volume: number): void {
        this.bgmVolume = Math.max(0, Math.min(1, volume));
        if (this.htmlAudio) {
            this.htmlAudio.volume = this.bgmVolume;
        }
    }

    getIntensity(): number {
        // HTML5 Audio에서는 beat detection 불가 - 항상 0 반환
        // (Web Audio API의 AnalyserNode 필요)
        return 0;
    }

    /**
     * 보스 BGM으로 전환
     */
    async switchToBossBGM(): Promise<void> {
        if (this.isBossMode) return;

        // 현재 트랙 저장
        this.savedTrack = this.currentTrack;
        this.isBossMode = true;

        // 기존 BGM 정지
        this.stop();

        // 보스 BGM 재생
        const prevSelected = this.selectedTrack;
        this.selectedTrack = 'A Memorial City';
        await this.play();
        this.selectedTrack = prevSelected;
    }

    /**
     * 일반 BGM으로 복귀
     */
    async switchToNormalBGM(): Promise<void> {
        if (!this.isBossMode) return;

        this.isBossMode = false;
        const trackToPlay = this.savedTrack || this.selectedTrack;

        // 기존 BGM 정지
        this.stop();

        // 일반 BGM 재생
        const prevSelected = this.selectedTrack;
        this.selectedTrack = trackToPlay;
        await this.play();
        this.selectedTrack = prevSelected;

        this.savedTrack = null;
    }

    /**
     * 보스 모드 여부 반환
     */
    isBossModeActive(): boolean {
        return this.isBossMode;
    }
}
